/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import "./Application.css";
import MetricsDisplay from "../MetricsDisplay/MetricsDisplay";
import useCurrentContact from "../../hooks/useCurrentContact";
import { organizeAttributes, getAttributeValueByName } from "../../services/Attributes";
import { ThemeProvider } from "react-bootstrap";
import TabLayout from "../Layouts/TabLayout";
import { contactEndedAction, contactChangedAction } from "../../services/IFrameService";
import { Prompt } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { useContactData } from "../../context/contactData";
import useAgentStats from "../../hooks/useAgentStats";
import useTeamsSync from "../../hooks/useTeamsSync";
/**
 * This component is called in the start file and is responsible for
 * creating all of the panels aside from the CCP panel.
 */
export const Application = ({ layoutConfiguration, renderToastContent, teamsSyncEnabled }) => {
	const [contactNotes, setContactNotes] = React.useState({});
	const { removeContactData } = useContactData();
	const { agentStats } = useAgentStats();
	useTeamsSync(agentStats, teamsSyncEnabled);

	const setNotesForContact = React.useCallback((newContactNote, contactId) => {
		setContactNotes((oldNotes) => {
			let newContactNotes = { ...oldNotes };
			newContactNotes[contactId] = newContactNote;
			return newContactNotes;
		});
	}, []);

	const removeContactNotes = React.useCallback((contactId) => {
		setContactNotes((oldNotes) => {
			if (oldNotes[contactId]) {
				let newContactNotes = { ...oldNotes };
				delete newContactNotes[contactId];
				return newContactNotes;
			}
			return oldNotes;
		});
	}, []);

	const onContactEnd = React.useCallback(
		(contactId) => {
			removeContactData(contactId);
			removeContactNotes(contactId);
			contactEndedAction(contactId);
		},
		[removeContactNotes, removeContactData],
	);

	const contact = useCurrentContact(onContactEnd);
	const [caller, setCaller] = React.useState(null);
	const [attributes, setAttributes] = React.useState(contact ? organizeAttributes(contact.attributes) : null);

	React.useEffect(() => {
		if (contact) {
			if (contact.attributes && !contactNotes[contact.contactId]) {
				const notes = getAttributeValueByName(contact.attributes, "notes", "");
				const dispositionId = getAttributeValueByName(contact.attributes, "hidden_dispositionId", "");
				const subDispositionId = getAttributeValueByName(contact.attributes, "hidden_subDispositionId", "");
				setNotesForContact({ notes, dispositionId, subDispositionId }, contact.contactId);
			}
		}
	}, [contact, contactNotes, setNotesForContact]);

	React.useEffect(() => {
		if (contact) {
			contactChangedAction(contact);
		}

		if (contact && contact.phone && contact.phone !== caller) {
			setCaller(contact.phone);
			setAttributes(organizeAttributes(contact.attributes));
		} else if (!contact && caller !== null) {
			setCaller(null);
			setAttributes(null);
		}
	}, [contact, caller]);

	return (
		<ThemeProvider prefixes={{ "card-header": "perficient-background" }}>
			<Prompt
				when={contact !== null}
				message={() =>
					`Leaving this page may disrupt the connection to the current contact. Are you sure you want to leave?`
				}
			/>
			<div
				style={{ display: "flex", flexDirection: "column", width: "100%", minHeight: "718px", position: "relative" }}
			>
				<div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", position: "absolute" }}>
					<div style={{ margin: "0px 15px" }}>
						<MetricsDisplay />
					</div>
					{contact || layoutConfiguration.staticTabs ? (
						<div className="contact-data-container">
							<div
								style={{
									padding: "0px 5px 0px 25px",
									marginTop: 15,
									display: "flex",
									flexDirection: "column",
									flex: 1,
								}}
							>
								<TabLayout
									tabConfig={layoutConfiguration}
									contact={contact}
									attributes={attributes}
									caller={caller}
									contactNotes={contactNotes}
									setNotesForContact={setNotesForContact}
									renderToastContent={renderToastContent}
								/>
							</div>
						</div>
					) : (
						<div className="no-contact-msg">
							<div className="d-inline w-100">
								<h4>No active contact</h4>
								<span>Additional contact information will be shown here when you're in a call or chat.</span>
							</div>
						</div>
					)}
				</div>
			</div>
		</ThemeProvider>
	);
};
